/**
 * Wishlist toggle — the storefront half of the app-proxy write path.
 *
 * The theme can only READ customer metafields (new customer accounts killed the customers/* Liquid
 * write path), so every mutation goes to the app proxy, which verifies Shopify's signature and is
 * the only thing that knows the customer id. See velara-apps/velara-customer-bridge.
 *
 * Reference flow (nove25.net, iqitwishlist in bottom-d4bab01438.js):
 *   click  → POST {process:'add', idProduct, idProductAttribute}
 *   guest  → {success:false, data:{type:'notLogged'}} → open the login modal, store nothing
 *   ok     → fill the heart, show #iqitwishlist-notification for 3500ms
 *
 * Two deliberate departures from their JS, both invisible in the UI:
 *   · they `addClass('iqitwishlist-added')` BEFORE checking success, so a failed add still looks
 *     saved. We only paint what the server confirmed.
 *   · they `nbProducts++` on every add, duplicates included. We use the count the server returns.
 *
 * GUEST WISHLIST (settings.wishlist_allow_guests) is a DIVERGENCE from the reference, which gates
 * guests behind a login modal and stores nothing. When enabled, a guest's list lives in
 * localStorage and is merged into their account the first time they load a page signed in. It is a
 * setting because a reviewer comparing against nove25.net would otherwise flag the difference.
 */
(function () {
  'use strict';

  var TOAST_MS = 3500; // measured: their setTimeout(…, 3500)
  var STORE_KEY = 'velara:wishlist';
  var STORE_VERSION = 2; // v1 = {p,v}; v2 adds {h} (handle) so guest cards can be rendered
  var MAX_ITEMS = 100; // mirrors MAX_ITEMS in the app — a guest list must not exceed what merge accepts

  var cfgEl = document.getElementById('velara-wishlist-config');
  var CFG = {
    endpoint: cfgEl ? cfgEl.getAttribute('data-endpoint') : '',
    loggedIn: cfgEl ? cfgEl.getAttribute('data-logged-in') === '1' : false,
    allowGuests: cfgEl ? cfgEl.getAttribute('data-allow-guests') === '1' : false,
  };

  function byId(id) {
    return document.getElementById(id);
  }

  /* ── guest store ──────────────────────────────────────────────────────────────────────────────
     Same shape as the server list: newest first, ONE entry per product (so the pane can't draw a
     duplicated card), variant kept so the saved size survives the handover. Every access is
     try/caught: localStorage throws in private mode and when the quota is full, and a wishlist is
     never worth breaking the page over. */
  function readGuest() {
    try {
      var raw = window.localStorage.getItem(STORE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      // v1 blobs are READ, not discarded — a shopper's saves outlive a schema bump. They lack a
      // handle so they cannot be rendered as cards, but they still merge on sign-in (merge needs only
      // variant ids), which is the outcome that actually matters.
      if (!parsed || !Array.isArray(parsed.items)) return [];
      if (parsed.version !== 1 && parsed.version !== STORE_VERSION) return [];
      return parsed.items.filter(function (it) {
        return it && /^[0-9]+$/.test(String(it.p)) && /^[0-9]+$/.test(String(it.v));
      });
    } catch (e) {
      return [];
    }
  }

  function writeGuest(items) {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify({ version: STORE_VERSION, items: items }));
    } catch (e) {
      /* private mode / quota — the heart stays painted for this page and that is the best we can do */
    }
  }

  function clearGuest() {
    try {
      window.localStorage.removeItem(STORE_KEY);
    } catch (e) {
      /* ignore */
    }
  }

  function guestToggle(productId, variantId, adding, handle) {
    var items = readGuest().filter(function (it) {
      return String(it.p) !== String(productId);
    });
    if (adding) {
      items.unshift({ p: String(productId), v: String(variantId), h: String(handle || '') });
      // Oldest saves fall off the end rather than the newest click being refused — a guest has no
      // way to curate a list they cannot see.
      if (items.length > MAX_ITEMS) items = items.slice(0, MAX_ITEMS);
    }
    writeGuest(items);
    return items.length;
  }

  /* ── chrome ───────────────────────────────────────────────────────────────────────────────── */

  var toastTimer = null;
  function toast(message) {
    var box = byId('velara-wishlist-toast');
    if (!box) return;
    var label = box.querySelector('[data-wishlist-toast-text]');
    if (label && message) label.textContent = message;
    box.classList.add('is-visible');
    if (toastTimer) clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      box.classList.remove('is-visible');
    }, TOAST_MS);
  }

  var lastFocus = null;

  function openModal() {
    var modal = byId('velara-wishlist-modal');
    // No modal rendered — send the guest to the login page rather than swallowing the click.
    if (!modal) {
      var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
      window.location.href = root + 'account/login';
      return;
    }
    lastFocus = document.activeElement;
    modal.classList.add('is-open');
    var focusable = modal.querySelector('a, button, input');
    if (focusable) focusable.focus();
  }

  function closeModal() {
    var modal = byId('velara-wishlist-modal');
    if (!modal) return;
    modal.classList.remove('is-open');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  /**
   * Paint every heart for this product, not just the one clicked: the same product can appear on a
   * PLP card, in a related-products rail and on the PDP at once, and they must not disagree.
   *
   * `animate` is opt-in so the beat fires ONLY on a resolved user action. Hydrating a guest's saved
   * hearts at page load also calls paint(), and animating there would set off a burst of beats on
   * every navigation.
   */
  function paint(productId, inWishlist, animate) {
    var all = document.querySelectorAll('[data-wishlist-toggle][data-product-id="' + productId + '"]');
    for (var i = 0; i < all.length; i++) {
      var btn = all[i];
      btn.classList.toggle('is-in-wishlist', inWishlist);
      btn.setAttribute('aria-pressed', inWishlist ? 'true' : 'false');
      var label = inWishlist ? btn.getAttribute('data-label-added') : btn.getAttribute('data-label');
      if (label) {
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
      }
      if (animate) beat(btn, inWishlist);
    }
  }

  /** Retrigger-safe: strip both classes and force a reflow, so a rapid re-click replays the beat. */
  function beat(btn, added) {
    var cls = added ? 'is-just-added' : 'is-just-removed';
    btn.classList.remove('is-just-added', 'is-just-removed');
    void btn.offsetWidth; // reflow — without it the same class re-added in one frame does nothing
    btn.classList.add(cls);
    var done = function (e) {
      // The animation is on the icon, so the event bubbles from a child; ignore anything else.
      if (e && e.target && !e.target.closest('.velara-wishlist-btn__icon')) return;
      btn.classList.remove(cls);
      btn.removeEventListener('animationend', done);
    };
    btn.addEventListener('animationend', done);
    // Belt and braces: if the animation never runs (reduced motion, display:none), still clean up.
    setTimeout(function () { btn.classList.remove(cls); }, 600);
  }

  /** Publish the count so a brand can render a badge; the reference shows none, so nothing here does. */
  function publishCount(count) {
    if (typeof count !== 'number') return;
    window.VelaraWishlist = window.VelaraWishlist || {};
    window.VelaraWishlist.count = count;
    var nodes = document.querySelectorAll('[data-wishlist-count]');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = String(count);
      nodes[i].hidden = count === 0;
    }
    document.dispatchEvent(new CustomEvent('velara:wishlist:change', { detail: { count: count } }));
  }

  function post(endpoint, payload) {
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r
        .json()
        .catch(function () {
          return { ok: r.ok };
        })
        .then(function (data) {
          return { status: r.status, data: data || {} };
        });
    });
  }

  /** The guest path, extracted because a CACHED page can send a signed-in shopper down it. */
  function saveAsGuest(btn, productId, variantId, adding) {
    var count = guestToggle(productId, variantId, adding, btn.getAttribute('data-product-handle'));
    paint(productId, adding, true);
    publishCount(count);
    if (adding) toast(btn.getAttribute('data-toast') || null);
    updateGuestNotice(count);
  }

  /* ── clicks ───────────────────────────────────────────────────────────────────────────────── */

  document.addEventListener('click', function (e) {
    var closest = e.target.closest ? e.target.closest.bind(e.target) : null;
    if (!closest) return;

    if (closest('[data-wishlist-modal-close]')) {
      e.preventDefault();
      closeModal();
      return;
    }

    var btn = closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();

    var endpoint = btn.getAttribute('data-endpoint') || CFG.endpoint;
    if (!endpoint) return;

    var productId = btn.getAttribute('data-product-id');
    var variantId = btn.getAttribute('data-variant-id');
    var adding = !btn.classList.contains('is-in-wishlist');
    var loggedIn = btn.getAttribute('data-logged-in') === '1';

    // ── guest ────────────────────────────────────────────────────────────────────────────────
    if (!loggedIn) {
      // Reference behaviour: no guest list, straight to the login gate. Skipping the round-trip
      // means the modal opens instantly; the server would answer notLogged anyway.
      if (!CFG.allowGuests) {
        openModal();
        return;
      }
      saveAsGuest(btn, productId, variantId, adding);
      return;
    }

    // ── signed in ────────────────────────────────────────────────────────────────────────────
    if (btn.classList.contains('is-pending')) return;
    btn.classList.add('is-pending');

    post(endpoint, adding ? { op: 'add', product_id: productId, variant_id: variantId } : { op: 'remove', product_id: productId })
      .then(function (res) {
        if (res.data.reason === 'notLogged' || res.status === 401) {
          /* `data-logged-in` is compiled into the page, so a CACHED page can claim a shopper is signed
             in when they are not (or the reverse). The server is the only authority — it reads the
             customer id from the signed proxy params. So when it says notLogged, believe it: fall back
             to the guest path instead of bouncing someone to a login page they did not ask for. Their
             saves then merge into the account on the next signed-in load, so the mismatch self-heals.
             The reverse mismatch already self-heals: a page cached as a guest saves locally, and the
             merge folds it in later. */
          if (CFG.allowGuests) {
            saveAsGuest(btn, productId, variantId, adding);
          } else {
            openModal();
          }
          return;
        }
        if (!res.data.ok) return;

        // Trust the server's own view of the list rather than assuming the click won.
        var now = typeof res.data.inWishlist === 'boolean' ? res.data.inWishlist : adding;
        paint(productId, now, true);
        publishCount(res.data.count);
        if (now && adding) toast(btn.getAttribute('data-toast') || null);
      })
      .catch(function () {
        /* network error: leave the heart as it was — a wrong filled heart is worse than no feedback */
      })
      .finally(function () {
        btn.classList.remove('is-pending');
      });
  });

  document.addEventListener('keydown', function (e) {
    var modal = byId('velara-wishlist-modal');
    if (e.key === 'Escape') {
      closeModal();
      return;
    }
    /* FOCUS TRAP. `aria-modal="true"` is a promise to assistive tech that the rest of the page is
       inert, and Tab was walking straight out of the dialog into the page behind it — so the promise
       was false. Cycle within the dialog instead. */
    if (e.key !== 'Tab' || !modal || !modal.classList.contains('is-open')) return;
    var focusables = modal.querySelectorAll('a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    // Focus may sit outside the dialog entirely (the page was focused before it opened), in which case
    // the first Tab must land inside rather than continuing through the background.
    if (!modal.contains(document.activeElement)) {
      e.preventDefault();
      (e.shiftKey ? last : first).focus();
      return;
    }
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /**
   * PDP: the saved size must follow the size the shopper picked. The reference does the same thing
   *   prestashop.on('updatedProduct', …) → data-id-product-attribute = the new variant
   * Ours listens to `velara:variant:change`, dispatched by assets/product-form.js.
   */
  document.addEventListener('velara:variant:change', function (e) {
    var detail = (e && e.detail) || {};
    var variant = detail.variant;
    if (!variant || !variant.id) return;
    // Scope to the product that actually changed — a page can hold several pickers.
    var sel = '[data-wishlist-toggle]';
    if (detail.productId) sel += '[data-product-id="' + detail.productId + '"]';
    var nodes = document.querySelectorAll(sel);
    for (var i = 0; i < nodes.length; i++) nodes[i].setAttribute('data-variant-id', String(variant.id));
  });

  /* ── guest notice in the account pane ─────────────────────────────────────────────────────────
     A guest's list cannot be rendered as product cards: Liquid never sees localStorage, so the pane
     shows a count and a sign-in prompt instead. Building the grid client-side would mean a second
     copy of product-card markup, which is exactly the drift we avoid elsewhere. */
  function updateGuestNotice(count) {
    var notice = document.querySelector('[data-wishlist-guest-notice]');
    if (!notice) return;
    var n = typeof count === 'number' ? count : readGuest().length;
    var slot = notice.querySelector('[data-wishlist-guest-count]');
    if (slot) slot.textContent = String(n);
    // Liquid rendered both plural forms onto the element (JS owns the number, Liquid owns the copy).
    var phrase = notice.querySelector('[data-wishlist-guest-label]');
    var form = n === 1 ? notice.getAttribute('data-label-one') : notice.getAttribute('data-label-other');
    if (phrase && form) phrase.textContent = form;
    notice.hidden = n === 0;
    var empty = document.querySelector('[data-wishlist-empty]');
    if (empty) empty.hidden = n > 0;
    return n;
  }

  /* ── rendering a GUEST's list ──────────────────────────────────────────────────────────────
     A guest's products live in localStorage, which Liquid cannot see — so the browser asks Liquid
     for each card instead of building one itself:

         GET /products/<handle>?view=wishlist-item&variant=<id>

     That returns the REAL card from the REAL snippet (templates/product.wishlist-item.liquid), so a
     guest's pane is markup-identical to a signed-in shopper's and there is no second copy of
     product-card to drift. Prices, money format, availability and locale are resolved server-side,
     which a client-side JSON render would get wrong on any multi-market store.

     Responses are cached in sessionStorage: they are per-PRODUCT (not per-customer), so caching them
     is safe, and reopening the pane costs nothing. */
  var FRAGMENT_CACHE = 'velara:wishlist:frag';

  function cachedFragment(key) {
    try { return window.sessionStorage.getItem(FRAGMENT_CACHE + ':' + key); } catch (e) { return null; }
  }
  function cacheFragment(key, html) {
    try { window.sessionStorage.setItem(FRAGMENT_CACHE + ':' + key, html); } catch (e) { /* full/private */ }
  }

  function fetchItem(entry, view) {
    var v = view || 'wishlist-item';
    // The view is part of the cache key: the account pane asks for a CARD and the shared page for a
    // ROW, and keying on handle@variant alone would let one serve the other's markup.
    var key = entry.h + '@' + entry.v + '@' + v;
    var hit = cachedFragment(key);
    if (hit) return Promise.resolve(hit);
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    var url = root + 'products/' + encodeURIComponent(entry.h) + '?view=' + encodeURIComponent(v) + '&variant=' + encodeURIComponent(entry.v);
    return fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (r) {
        // 404 = deleted or unpublished. Drop it, mirroring the server-side prune of dead references,
        // rather than leaving a hole the shopper cannot clear.
        if (!r.ok) return null;
        return r.text();
      })
      .then(function (html) {
        if (html) cacheFragment(key, html);
        return html;
      })
      .catch(function () { return null; });
  }

  /**
   * Render a list of {h,v} entries into a container by asking LIQUID for each card. Shared by the
   * guest pane and the public shared-wishlist page, so a shared list is markup-identical to a real
   * one and there is still exactly one copy of product-card.
   *
   * Returns the handles that failed to resolve, so the caller can decide what that means: the guest
   * pane prunes them from localStorage, a shared page just shows fewer cards.
   */
  function renderInto(container, entries, view) {
    var renderable = entries.filter(function (e) { return e.h; });
    if (!renderable.length) {
      container.innerHTML = '';
      container.hidden = true;
      return Promise.resolve([]);
    }
    return Promise.all(renderable.map(function (e) { return fetchItem(e, view); })).then(function (parts) {
      var dead = [];
      var html = '';
      parts.forEach(function (part, i) {
        if (part) html += part;
        else dead.push(renderable[i]);
      });
      container.innerHTML = html;
      container.hidden = !html;
      return dead;
    });
  }

  /** Fill the pane's grid with the guest's saved products. Idempotent — safe to call on every open. */
  function renderGuestList() {
    var pane = document.querySelector('[data-pane-type="wishlist"]');
    if (!pane) return Promise.resolve();
    var grid = pane.querySelector('[data-wishlist]');
    var empty = pane.querySelector('[data-wishlist-empty]');
    var notice = pane.querySelector('[data-wishlist-guest-notice]');
    var entries = readGuest();
    var renderable = entries.filter(function (e) { return e.h; });

    if (!grid) return Promise.resolve();
    if (!renderable.length) {
      // Reachable when every entry is a legacy v1 row (no handle, so nothing to fetch). Route the
      // notice through the same function as every other path — this branch used to set `hidden` by
      // hand and skip updateGuestNotice, which left the count and its plural form stale.
      grid.innerHTML = '';
      grid.hidden = true;
      updateGuestNotice(entries.length);
      if (empty) empty.hidden = entries.length > 0;
      return Promise.resolve();
    }

    return renderInto(grid, renderable).then(function (dead) {
      // Self-heal: products that no longer resolve leave the stored list too.
      if (dead.length) {
        var deadIds = dead.map(function (e) { return e.p; });
        writeGuest(readGuest().filter(function (e) { return deadIds.indexOf(e.p) === -1; }));
      }
      // Mark them so the overlay's remove handler edits localStorage instead of POSTing.
      var items = grid.querySelectorAll('[data-wishlist-item]');
      for (var i = 0; i < items.length; i++) items[i].setAttribute('data-wishlist-guest', '');
      if (empty) empty.hidden = items.length > 0;
      publishCount(readGuest().length);
      updateGuestNotice(readGuest().length);
    });
  }

  /** Remove one product from the guest list (called by the overlay's own remove handler). */
  function guestRemove(productId) {
    var items = readGuest().filter(function (it) { return String(it.p) !== String(productId); });
    writeGuest(items);
    paint(productId, false);
    publishCount(items.length);
    updateGuestNotice(items.length);
    return items.length;
  }

  // Public surface for the overlay section, which owns the pane and its remove control.
  window.VelaraWishlist = window.VelaraWishlist || {};
  window.VelaraWishlist.renderGuestList = renderGuestList;
  window.VelaraWishlist.guestRemove = guestRemove;
  window.VelaraWishlist.isGuestMode = function () { return CFG.allowGuests && !CFG.loggedIn; };
  window.VelaraWishlist.renderInto = renderInto;
  window.VelaraWishlist.readGuest = readGuest;
  // Exposed so the account overlay can confirm a copied share link through the SAME toast as every
  // other wishlist action, instead of growing a second notification style.
  window.VelaraWishlist.toast = toast;

  /* ── boot ─────────────────────────────────────────────────────────────────────────────────── */

  if (!CFG.allowGuests) return;

  if (!CFG.loggedIn) {
    // Hydrate hearts from the guest list. Server-rendered markup cannot know it, so every heart
    // arrives empty and is corrected here.
    var guest = readGuest();
    for (var i = 0; i < guest.length; i++) paint(guest[i].p, true);
    // Load-more / infinite scroll appends cards AFTER boot, and they arrive from Liquid with the
    // same empty hearts. Re-paint from the guest list whenever the grid grows.
    document.addEventListener('velara:products:appended', function () {
      var list = readGuest();
      for (var k = 0; k < list.length; k++) paint(list[k].p, true);
    });
    // Publish on hydrate too, not only on click: otherwise a brand's badge reads empty on every
    // fresh page load and only becomes correct once the shopper happens to click something.
    publishCount(guest.length);
    updateGuestNotice(guest.length);
    return;
  }

  // Signed in with a guest list still in localStorage → hand it over, once.
  var pending = readGuest();
  if (!pending.length || !CFG.endpoint) return;

  post(CFG.endpoint, {
    op: 'merge',
    variant_ids: pending.map(function (it) {
      return it.v;
    }),
  })
    .then(function (res) {
      if (!res.data.ok) return; // keep the list: a failed merge must not lose the shopper's saves
      clearGuest();
      publishCount(res.data.count);
      if (res.data.dropped) {
        // The account list was already at the cap, so we cannot know which entries landed —
        // re-render from the server rather than paint something that might be wrong.
        window.location.reload();
        return;
      }
      for (var j = 0; j < pending.length; j++) paint(pending[j].p, true);
    })
    .catch(function () {
      /* offline: the list stays in localStorage and the merge is retried on the next page load */
    });
})();
